import React, { useEffect, useRef, useState } from 'react'

const RevealOnScroll = ({ children, animation = 'scale' }) => {
    const containerRef = useRef(null)
    const [hasRevealed, setHasRevealed] = useState(false)

    useEffect(() => {
        if (!containerRef.current || hasRevealed) return

        const element = containerRef.current

        const observer = new IntersectionObserver(
            (entries) => {
                entries.forEach((entry) => {
                    if (entry.isIntersecting && entry.intersectionRatio >= 0.1) {
                        setHasRevealed(true)
                        observer.unobserve(element)
                    }
                })
            },
            {
                threshold: 0.1,
            }
        )

        observer.observe(element)
        return () => observer.disconnect()
    }, [hasRevealed])

    const transitionScale = 'opacity 700ms ease-out, transform 700ms ease-out'
    const transitionFade = 'opacity 700ms ease-out'
    const transitionClip = 'opacity 700ms ease-out, clip-path 700ms ease-out'
    const transitionSlideUp = 'transform 900ms cubic-bezier(0.16, 1, 0.3, 1)'

    let style
    if (animation === 'fade') {
        // For fade mode, avoid transform and will-change to preserve descendant backdrop-filter effects.
        style = hasRevealed
            ? undefined
            : { opacity: 0, transition: transitionFade }
    } else if (animation === 'clip') {
        // Clip-path reveal mimics a zoom-like feel without transforms.
        style = hasRevealed
            ? { opacity: 1, clipPath: 'inset(0%)', transition: transitionClip }
            : { opacity: 0, clipPath: 'inset(10% 10% 10% 10%)', transition: transitionClip }
    } else if (animation === 'slide-up') {
        // Slide-up animation keeps opacity at 1 to preserve backdrop-filter, only animates translateY
        // Uses a more pronounced slide distance and smooth easing for better visual appeal
        style = hasRevealed
            ? { opacity: 1, transform: 'translateY(0)', transition: transitionSlideUp }
            : { opacity: 1, transform: 'translateY(80px)', transition: transitionSlideUp }
    } else {
        style = hasRevealed
            ? { opacity: 1, transform: 'scale(1)', transition: transitionScale }
            : { opacity: 0, transform: 'scale(0.8)', transition: transitionScale }
    }

    return (
        <div ref={containerRef} style={style}>
            {children}
        </div>
    )
}

export default RevealOnScroll